import {
  AfterViewInit,
  Component,
  ElementRef,
  Inject,
  Input,
  NgZone,
  OnChanges,
  PLATFORM_ID,
  SimpleChanges,
  ViewChild,
  ChangeDetectionStrategy,
} from '@angular/core';
import { DatePipe, isPlatformBrowser } from '@angular/common';
import * as am5 from '@amcharts/amcharts5';
import * as am5xy from '@amcharts/amcharts5/xy';
import am5themes_Animated from '@amcharts/amcharts5/themes/Animated';
import { OlxAdvertFullStatisticsListItem } from '../../../../modules/crm/power-olx-v2/interfaces/power-olx-v2.interface';
import { TranslateService } from '@ngx-translate/core';

interface Daily {
  date: Date;
  messages: number;
  page_views: number;
}

@Component({
  selector: 'app-advert-statistics-chart',
  templateUrl: './advert-statistics-chart.component.html',
  styleUrl: './advert-statistics-chart.component.scss',
  changeDetection: ChangeDetectionStrategy.Eager,
  standalone: false,
})
export class AdvertStatisticsChartComponent implements AfterViewInit, OnChanges {
  @Input() statistics: OlxAdvertFullStatisticsListItem[] = [];
  @ViewChild('chartEl') chartEl?: ElementRef<HTMLDivElement>;

  private pageIndex = 0;
  private root?: am5.Root;
  private chart?: am5xy.XYChart;
  private xAxis?: am5xy.CategoryAxis<am5xy.AxisRenderer>;
  private yAxis?: am5xy.ValueAxis<am5xy.AxisRenderer>;
  private sMessages?: am5xy.LineSeries;
  private sViews?: am5xy.LineSeries;

  constructor(
    private readonly zone: NgZone,
    private readonly datePipe: DatePipe,
    private readonly translate: TranslateService,
    @Inject(PLATFORM_ID) private readonly platformId: object,
  ) {}

  ngAfterViewInit() {
    this.tryBuildAndUpdate();
  }

  ngOnChanges(changes: SimpleChanges) {
    if (changes['statistics']) this.tryBuildAndUpdate();
  }

  get hasData(): boolean {
    return this.fullDaily.length > 0;
  }

  get totalPages(): number {
    return Math.max(1, Math.ceil(this.fullDaily.length / 7));
  }

  get canPrev() {
    return this.pageIndex < this.totalPages - 1;
  }

  get canNext() {
    return this.pageIndex > 0;
  }

  private tryBuildAndUpdate() {
    if (!this.hasData) {
      this.disposeChart();
      return;
    }
    if (!this.chartEl?.nativeElement) return;
    if (!this.root) this.buildChart();

    this.updateChartWindow();
  }

  prevPage() {
    if (this.canPrev) {
      this.pageIndex++;
      this.updateChartWindow();
    }
  }

  nextPage() {
    if (this.canNext) {
      this.pageIndex--;
      this.updateChartWindow();
    }
  }

  private get fullDaily(): Daily[] {
    const map = new Map<string, Daily>();
    for (const it of this.statistics || []) {
      const d = new Date(it.date);
      const key = d.toISOString().slice(0, 10);
      map.set(key, { date: new Date(key), messages: it.messages ?? 0, page_views: it.page_views ?? 0 });
    }
    if (map.size === 0) return [];

    const keys = [...map.values()].sort((a, b) => a.date.getTime() - b.date.getTime());
    const start = keys[0].date;
    const end = keys[keys.length - 1].date;
    const res: Daily[] = [];
    for (let d = new Date(start); d <= end; d.setDate(d.getDate() + 1)) {
      const k = d.toISOString().slice(0, 10);
      res.push(map.get(k) ?? { date: new Date(k), messages: 0, page_views: 0 });
    }
    return res;
  }

  protected get window(): Daily[] {
    const arr = this.fullDaily;
    const end = arr.length - this.pageIndex * 7;
    const start = Math.max(0, end - 7);
    return arr.slice(start, end);
  }

  private niceMaxY(vals: number[]): number {
    const max = Math.max(0, ...vals);
    if (max <= 5) return 5;
    return Math.ceil((max * 1.1) / 5) * 5;
  }

  private buildChart() {
    if (!isPlatformBrowser(this.platformId) || !this.chartEl) return;

    this.zone.runOutsideAngular(() => {
      this.root = am5.Root.new(this.chartEl!.nativeElement);
      this.root._logo?.dispose();
      this.root.setThemes([am5themes_Animated.new(this.root)]);

      this.chart = this.root.container.children.push(
        am5xy.XYChart.new(this.root, { panY: false, layout: this.root.verticalLayout }),
      );

      this.yAxis = this.chart.yAxes.push(
        am5xy.ValueAxis.new(this.root, { min: 0, renderer: am5xy.AxisRendererY.new(this.root, {}) }),
      );

      this.yAxis.get('renderer').labels.template.adapters.add('text', (text) => {
        return text === '0' ? '' : text;
      });

      this.xAxis = this.chart.xAxes.push(
        am5xy.CategoryAxis.new(this.root, {
          categoryField: 'category',
          renderer: am5xy.AxisRendererX.new(this.root, {}),
        }),
      );

      this.xAxis.get('renderer').setAll({
        minGridDistance: 12,
        cellStartLocation: 0,
        cellEndLocation: 1,
      });

      this.xAxis.get('renderer').labels.template.setAll({
        paddingTop: 2,
        paddingBottom: 0,
        centerX: am5.p50,
        centerY: am5.p50,
      });

      this.chart.setAll({ paddingLeft: 0, paddingRight: 0 });
      this.chart.plotContainer.setAll({ paddingLeft: 0, paddingRight: 0 });

      const VIEWS_COLOR = am5.color(0x1a73e8);
      const MSGS_COLOR = am5.color(0xf2994a);

      this.sViews = this.chart.series.push(
        am5xy.LineSeries.new(this.root, {
          name: this.translate.instant('views'),
          xAxis: this.xAxis,
          yAxis: this.yAxis,
          valueYField: 'page_views',
          categoryXField: 'category',
          fill: VIEWS_COLOR,
          stroke: VIEWS_COLOR,
        }),
      );
      this.sViews.set('tooltip', am5.Tooltip.new(this.root, { labelText: '{valueY}' }));
      this.sViews.strokes.template.setAll({ stroke: VIEWS_COLOR, strokeWidth: 2 });
      this.sViews.fills.template.setAll({ fill: VIEWS_COLOR, fillOpacity: 0.1 });
      this.sViews.bullets.push(() =>
        am5.Bullet.new(this.root!, {
          sprite: am5.Circle.new(this.root!, { radius: 3, fill: VIEWS_COLOR, stroke: VIEWS_COLOR }),
        }),
      );

      this.sMessages = this.chart.series.push(
        am5xy.LineSeries.new(this.root, {
          name: this.translate.instant('messages'),
          xAxis: this.xAxis,
          yAxis: this.yAxis,
          valueYField: 'messages',
          categoryXField: 'category',
          fill: MSGS_COLOR,
          stroke: MSGS_COLOR,
        }),
      );
      this.sMessages.set('tooltip', am5.Tooltip.new(this.root, { labelText: '{valueY}' }));
      this.sMessages.strokes.template.setAll({ stroke: MSGS_COLOR, strokeWidth: 2 });
      this.sMessages.fills.template.setAll({ fill: MSGS_COLOR, fillOpacity: 0.1 });
      this.sMessages.bullets.push(() =>
        am5.Bullet.new(this.root!, {
          sprite: am5.Circle.new(this.root!, { radius: 3, fill: MSGS_COLOR, stroke: MSGS_COLOR }),
        }),
      );

      const legend = this.chart.children.push(am5.Legend.new(this.root, {}));
      legend.data.setAll([this.sViews, this.sMessages]);

      this.chart.set('scrollbarX', am5.Scrollbar.new(this.root, { orientation: 'horizontal' }));
      this.chart.set('cursor', am5xy.XYCursor.new(this.root, {}));
    });
  }

  private disposeChart() {
    if (this.root) {
      this.root.dispose();
      this.root = undefined;
      this.chart = this.xAxis = this.yAxis = undefined;
      this.sMessages = this.sViews = undefined;
    }
  }

  private updateChartWindow() {
    if (!this.root || !this.chart || !this.xAxis || !this.yAxis || !this.sMessages || !this.sViews) return;
    const slice = this.window;
    if (!slice.length) return;

    const categories = slice.map((d) => this.datePipe.transform(d.date, 'dd.MM') as string);
    const data = slice.map((d, i) => ({
      category: categories[i],
      messages: d.messages ?? 0,
      page_views: d.page_views ?? 0,
    }));

    this.xAxis.data.setAll(data);
    this.sMessages.data.setAll(data);
    this.sViews.data.setAll(data);

    const maxY = this.niceMaxY(data.map((x) => Math.max(x.messages, x.page_views)));
    this.yAxis.set('max', maxY);
  }
}
